package org.example.project.registrierung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlin.math.abs
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.example.project.utils.DashboardVerlaufHintergrund
import org.example.project.settings.LogoSettings
import org.example.project.login.UnifiedAccessStyles
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─────────────────────────────────────────────────────────────────────────────
// Mobil-spezifisches StyleSheet  (<600 px)
// ─────────────────────────────────────────────────────────────────────────────

private object HandyRegistrierungStyles : StyleSheet() {

    val formInput by style {
        width(100.percent); minWidth(0.px); height(44.px)
        padding(0.px, 14.px); borderRadius(8.px)
        backgroundColor(Color.transparent); color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.18)")
        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)")
        boxSizing("border-box"); fontSize(15.px); textAlign("center")
        focus { property("border-color", "#ffffff"); outline("none") }
    }
    val formInputError by style { property("border-color", "#ff4d4d") }

    val primaryButton by style {
        width(100.percent); height(44.px); marginTop(14.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(Color.transparent); color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.22)")
        borderRadius(8.px); fontSize(15.px); fontWeight("600")
        cursor("pointer")
        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)")
        property("transition", "opacity 0.15s")
        hover { opacity(1) }
        disabled { opacity(0.4); cursor("not-allowed") }
    }

    val inlineRow by style {
        display(DisplayStyle.Flex); gap(8.px); width(100.percent)
    }

    val formContainer by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        property("flex-direction", "column")
        flexGrow(1)
        minHeight(0.px)
        gap(10.px)
    }

    val sectionTitle by style {
        color(Color.white); fontSize(18.px); fontWeight("700")
        marginTop(0.px); marginBottom(8.px); textAlign("center")
    }

    val sectionSubtitle by style {
        color(Color("#9aa0a6")); fontSize(13.px)
        marginTop(0.px); marginBottom(22.px); textAlign("center")
    }

    val errorText by style {
        fontSize(11.px); color(Color("#ff4d4d")); opacity(0.9)
        textAlign("center")
        property("pointer-events", "none")
    }

    val formCard by style {
        width(100.percent)
        overflowY("auto")
        padding(28.px); paddingBottom(40.px)
        border(0.px); borderRadius(20.px)
        color(Color.white); property("backdrop-filter", "blur(15px)")
        property("box-sizing", "border-box")
        display(DisplayStyle.Flex); property("flex-direction", "column")
        justifyContent(JustifyContent.FlexStart)
        textAlign("center")
    }

    val backArrow by style {
        display(DisplayStyle.None)
        width(22.px); height(22.px)
        cursor("pointer"); opacity(0.55)
        property("align-self", "flex-start"); marginBottom(6.px)
        property("filter", "brightness(0) invert(1)")
        hover { opacity(1) }
        property("transition", "opacity 0.15s")
    }

    val languageCard by style {
        width(100.percent); minHeight(52.px); borderRadius(10.px)
        border(0.px); backgroundColor(Color.transparent)
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
    }

    val languageStepHeader by style {
        position(Position.Fixed)
        left(50.percent)
        top(100.px)
        property("transform", "translateX(-50%)")
        width(100.percent)
        padding(0.px, 20.px)
        boxSizing("border-box")
    }

    val languageStepImage by style {
        width(100.percent)
        property("height", "auto")
        maxHeight(190.px)
        property("object-fit", "contain")
        display(DisplayStyle.Block)
        property("pointer-events", "none")
        flexShrink(1)
    }

    val loginImage by style {
        width(100.percent)
        property("height", "auto")
        property("object-fit", "contain")
        display(DisplayStyle.Block)
        marginTop(30.px)
        marginBottom(30.px)
        property("pointer-events", "none")
    }

    val languageStepDropdown by style {
        width(100.percent)
        maxWidth(240.px)
        marginTop(12.px)
        flexShrink(0)
    }

    val languageStepButton by style {
        position(Position.Fixed)
        left(20.px)
        right(20.px)
        bottom(50.px)
        property("width", "auto")
    }

    val centeredStepFields by style {
        position(Position.Fixed)
        left(20.px)
        right(20.px)
        property("width", "auto")
        top(50.percent)
        marginTop((-46).px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
    }

    val main by style {
        position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(40.px)
        property("z-index", "1")
        display(DisplayStyle.Flex); property("flex-direction", "column")
        alignItems(AlignItems.Center); justifyContent(JustifyContent.FlexStart)
        padding(30.px, 20.px, 0.px, 20.px); boxSizing("border-box")
        overflow("auto")
    }

    val logo by style {
        width(192.px); marginTop(50.px); marginBottom(28.px)
        property("object-fit", "contain"); cursor("pointer")
    }

    val entryRow by style {
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Row)
        gap(40.px); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        marginBottom(24.px); flexShrink(0)
    }

    val entryBtn by style {
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(8.px)
        height(44.px); boxSizing("border-box")
        color(Color.white); fontSize(16.px); fontWeight("600")
        cursor("pointer"); opacity(0.85)
        hover { opacity(1) }
        property("transition", "opacity 0.15s")
    }

}

// ─────────────────────────────────────────────────────────────────────────────
// Slot-Implementierung für Mobil
// ─────────────────────────────────────────────────────────────────────────────

private fun buildHandySlots(
    activeView: String,
    regStep: Int,
): FahrerUiSlots = FahrerUiSlots(

    textInput = { type, ph, value, error, onInput, onEnter, layout ->
        Input(type, attrs = {
            classes(HandyRegistrierungStyles.formInput)
            if (error) classes(HandyRegistrierungStyles.formInputError)
            placeholder(ph)
            this.value(value)
            onInput { onInput(it.value) }
            if (onEnter != null) onKeyDown { if (it.key == "Enter") { it.preventDefault(); onEnter() } }
            layout?.invoke(this)
        })
    },

    primaryButton = { label, enabled, loading, onClick ->
        Button(attrs = {
            classes(HandyRegistrierungStyles.primaryButton)
            if (activeView == "REGISTER" && regStep >= 1) {
                style {
                    position(Position.Fixed)
                    left(20.px)
                    right(20.px)
                    bottom(50.px)
                    property("width", "auto")
                    marginTop(0.px)
                }
            }
            if (!enabled || loading) disabled()
            onClick { onClick() }
        }) { Text(label) }
    },

    inlineRow = { content ->
        Div({ classes(HandyRegistrierungStyles.inlineRow) }) { content() }
    },

    formContainer = { content ->
        Div({
            classes(HandyRegistrierungStyles.formContainer)
            if (activeView == "LOGIN") {
                style {
                    flexGrow(0)
                    gap(10.px)
                }
            }
            if (activeView == "REGISTER" && regStep >= 2) {
                style {
                    position(Position.Fixed)
                    left(20.px)
                    right(20.px)
                    property("width", "auto")
                    top(50.percent)
                    marginTop((-46).px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(10.px)
                }
            }
        }) { content() }
    },

    sectionTitle = { title, subtitle ->
        Div({
            if (activeView == "REGISTER" && regStep >= 1) {
                style {
                    position(Position.Fixed)
                    left(50.percent)
                    top(100.px)
                    property("transform", "translateX(-50%)")
                    width(100.percent)
                    padding(0.px, 20.px)
                    boxSizing("border-box")
                }
            }
        }) {
            H2({ classes(HandyRegistrierungStyles.sectionTitle) }) { Text(title) }
            if (activeView == "LOGIN") {
                P({
                    classes(HandyRegistrierungStyles.sectionSubtitle)
                    style { marginBottom(0.px) }
                }) { Text(subtitle) }
                Img(src = "images/fahrer.png", attrs = {
                    classes(HandyRegistrierungStyles.loginImage)
                })
            } else {
                P({ classes(HandyRegistrierungStyles.sectionSubtitle) }) { Text(subtitle) }
            }
        }
    },

    errorText = { msg ->
        Span({ classes(HandyRegistrierungStyles.errorText) }) { Text(msg) }
    },

    backArrow = { onClick ->
        // Mobile registration uses swipe-left for back navigation.
    },

    languagePlaceholder = { selectedLanguage, onLanguage ->
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flex(1)
                minHeight(0.px)
                width(100.percent)
            }
        }) {
            Img(src = "images/fahrer.png", attrs = {
                classes(HandyRegistrierungStyles.languageStepImage)
            })
            Div({
                classes(HandyRegistrierungStyles.languageCard)
                classes(HandyRegistrierungStyles.languageStepDropdown)
            }) {
                FahrerLanguageDropdown(selectedLanguage, onLanguage)
            }
        }
    },
)

// ─────────────────────────────────────────────────────────────────────────────
// Mobil-Layout
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun MobilFahrerRegistrierungLayout(
    activeView: String,
    loginState: FahrerLoginFlowState,
    regState: FahrerRegFlowState,
    loginCallbacks: FahrerLoginCallbacks,
    regCallbacks: FahrerRegCallbacks,
    registeredCompanies: List<All>,
    onSetView: (String) -> Unit,
    companionMode: Boolean = false,
) {
    Style(HandyRegistrierungStyles)
    Style(NetzwerkLoginStyles)
    Style(UnifiedAccessStyles)
    Style(object : StyleSheet() {
        init {
            "html, body" style {
                margin(0.px); padding(0.px)
                width(100.percent); height(100.percent)
                overflow("hidden")
            }
        }
    })
    DashboardVerlaufHintergrund()

    val slots = buildHandySlots(activeView, regState.step)
    val swipeBack: (() -> Unit)? = if (activeView == "REGISTER" && regState.step > 1) {
        {
            when (regState.step) {
                4 -> regCallbacks.onStep(if (regState.registrationMode == "snapkey") 2 else 3)
                else -> regCallbacks.onStep(regState.step - 1)
            }
        }
    } else {
        null
    }
    var swipeStartX by remember { mutableStateOf(0.0) }
    var swipeStartY by remember { mutableStateOf(0.0) }

    Main({
        classes(HandyRegistrierungStyles.main)
        if (companionMode) classes(UnifiedAccessStyles.stage)
    }) {
        if (activeView == "NONE") Img(src = LogoSettings.fahrerLoginHeroLogo, attrs = { classes(HandyRegistrierungStyles.logo) })

        if (activeView == "NONE") {
            // Entry-Buttons
            Div({ classes(HandyRegistrierungStyles.entryRow) }) {
                Div({
                    classes(HandyRegistrierungStyles.entryBtn)
                    onClick { onSetView("LOGIN") }
                }) {
                    Span(attrs = {
                        ref { el ->
                            el.innerHTML = """<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-3.33 0-10 1.672-10 5v1h20v-1c0-3.328-6.67-5-10-5z"/></svg>"""
                            onDispose {}
                        }
                    }) {}
                    Text(LocalizationManager.get("driver_login_button"))
                }

                Div({ style { width(1.px); height(24.px); backgroundColor(Color("rgba(255,255,255,0.25)")) } })

                Div({
                    classes(HandyRegistrierungStyles.entryBtn)
                    onClick { onSetView("REGISTER") }
                }) {
                    Span(attrs = {
                        ref { el ->
                            el.innerHTML = """<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M15 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm-9 2v2H3v2h3v2l3-3-3-3zm9 0c-3.33 0-10 1.672-10 5v1h20v-1c0-3.328-6.67-5-10-5z"/></svg>"""
                            onDispose {}
                        }
                    }) {}
                    Text(LocalizationManager.get("driver_register_button"))
                }
            }

            // Eigenständiges DRIVER-Hero-Bild; keine NET-Karte im Handy-Layout.
            Div({
                style {
                    flex(1); width(100.percent); minHeight(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    gap(12.px)
                }
            }) {
                Img(src = "images/fahrer.png", attrs = {
                    style {
                        width(90.percent)
                        height(100.percent)
                        property("max-height", "42vh")
                        property("object-fit", "contain")
                        display(DisplayStyle.Block)
                    }
                })
            }

        } else {
            // Formular-Karte
            Div({
                style {
                    flex(1); width(100.percent); minHeight(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    gap(12.px)
                }
                onTouchStart {
                    val touch = it.touches.item(0) ?: return@onTouchStart
                    swipeStartX = touch.clientX.toDouble()
                    swipeStartY = touch.clientY.toDouble()
                }
                onTouchEnd {
                    val touch = it.changedTouches.item(0) ?: return@onTouchEnd
                    val deltaX = touch.clientX.toDouble() - swipeStartX
                    val deltaY = touch.clientY.toDouble() - swipeStartY
                    if (deltaX > 80 && abs(deltaY) < 80) {
                        swipeBack?.invoke()
                    }
                }
            }) {
                Div({
                    classes(HandyRegistrierungStyles.formCard)
                    if (activeView == "REGISTER") classes(UnifiedAccessStyles.panel, UnifiedAccessStyles.mobilePanel)
                    if (activeView == "REGISTER") style {
                        height(76.vh)
                        maxHeight(680.px)
                        flexShrink(0)
                        justifyContent(JustifyContent.FlexStart)
                    }
                    if (activeView == "LOGIN") style {
                        justifyContent(JustifyContent.Center)
                    }
                }) {
                    if (activeView == "REGISTER") Img(
                        src = LogoSettings.desktopRegistrationHeaderLogo,
                        attrs = {
                            classes(UnifiedAccessStyles.logo)
                            style { flexShrink(0) }
                        }
                    )
                    when (activeView) {
                        "LOGIN"    -> FahrerLoginFlow(loginState, loginCallbacks, slots, if (companionMode) null else { { onSetView("REGISTER") } })
                        "REGISTER" -> {
                            FahrerRegFlow(regState, regCallbacks, slots) { onSetView("LOGIN") }
                        }
                    }
                }
            }
        }
    }

}

